
import CryptoJS from 'crypto-js'
import { Language } from "../types";
import { dbService } from "./dbService";
export interface TokenMetrics {
  promptTokens: number;
  candidateTokens: number;
  totalTokens: number;
}

const PRICING = {
  TEXT_INPUT: 0.1 / 1000000,
  TEXT_OUTPUT: 0.4 / 1000000,
  IMAGE_PER_REQUEST: 0.02 
};

const sessionStats = {
  totalPromptTokens: 0,
  totalCandidateTokens: 0,
  totalOverallTokens: 0,
  totalCost: 0,
  lastCost: 0
};

/**
 * 图片 URL 转 base64（浏览器端）
 * url 图片在线地址
 *  base64 字符串
 */
const imageUrlToBase64 = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // 关键：避免 canvas 污染
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      const base64 = canvas.toDataURL('image/png')
      resolve(base64)
    }
    img.onerror = reject
    img.src = url
  })
}

/**
 * 记录 Token 使用情况和成本估算
 */
const logTokenUsage = (label: string, metrics: TokenMetrics, isImage: boolean = false) => {
  let currentCost = 0;
  if (isImage) {
    currentCost = PRICING.IMAGE_PER_REQUEST;
  } else {
    currentCost = (metrics.promptTokens * PRICING.TEXT_INPUT) + (metrics.candidateTokens * PRICING.TEXT_OUTPUT);
  }

  sessionStats.totalPromptTokens += metrics.promptTokens;
  sessionStats.totalCandidateTokens += metrics.candidateTokens;
  sessionStats.totalOverallTokens += metrics.totalTokens;
  sessionStats.totalCost += currentCost;
  sessionStats.lastCost = currentCost;

  const labelBg = isImage ? '#8b5cf6' : '#3b82f6'; 
  const costColor = '#10b981'; 

  console.log(
    `%c AI CREATOR %c ${label.toUpperCase()} %c +${metrics.totalTokens} Tokens %c $${currentCost.toFixed(5)} %c 累计消耗: $${sessionStats.totalCost.toFixed(4)} `,
    'background: #0f172a; color: #fff; border-radius: 3px 0 0 3px; padding: 2px 5px; font-weight: bold;',
    `background: ${labelBg}; color: #fff; padding: 2px 5px; font-weight: bold;`,
    'background: #f1f5f9; color: #475569; padding: 2px 5px;',
    `color: ${costColor}; font-weight: bold; padding: 2px 5px;`,
    `background: #ef4444; color: #fff; border-radius: 0 3px 3px 0; padding: 2px 5px; font-weight: bold;`
  );
};

/**
 * 核心：详细记录 API 错误，特别识别 429 配额具体限制
 */
const logApiError = (error: any, context: string) => {
  const message = error?.message || "";
  const status = error?.status || (message.includes('429') ? 429 : (message.includes('500') ? 500 : 'Unknown'));
  
  console.group(`%c 🛑 AI API 错误报告 %c ${context} `, 'background: #ef4444; color: #fff; font-weight: bold; padding: 2px 5px;', 'background: #fee2e2; color: #b91c1c; padding: 2px 5px;');
  console.error(`状态码: ${status}`);
  console.error(`错误详情: ${message}`);
  
  if (status === 429 || message.includes('quota') || message.includes('limit')) {
    let limitType = "通用配额限制 (General Quota)";
    let advice = "请检查您的 API 计划。如果是免费层级，请稍等片刻再试。";

    if (message.includes("RPM")) {
      limitType = "RPM (Requests Per Minute) - 每分钟请求数超限";
      advice = "您在 60 秒内发送了太多请求。请降低操作频率，或升级至付费计划以提高并发。";
    } else if (message.includes("TPM")) {
      limitType = "TPM (Tokens Per Minute) - 每分钟 Token 消耗量超限";
      advice = "单次请求的数据量过大或短时间内发送了大量长文本。尝试缩短提示词。";
    } else if (message.includes("RPD")) {
      limitType = "RPD (Requests Per Day) - 每日请求总数超限";
      advice = "您已达到今天的免费额度上线（通常为 1500 次）。请明天再试或绑定结算账户。";
    }

    console.warn(`%c ⚠️ 检测到具体限制: ${limitType} `, 'background: #f59e0b; color: #fff; font-weight: bold; padding: 4px; border-radius: 4px;');
    console.info(`%c 建议解决方案: ${advice} `, 'color: #d97706; font-weight: bold;');
    console.info('监控地址: https://aistudio.google.com/app/plan_and_billing');
  } else if (status === 500) {
    console.warn('%c ☁️ 服务器繁忙 ', 'background: #64748b; color: #fff; padding: 2px 5px;');
    console.info('Google 服务器目前压力较大，已自动发起指数退避重试...');
  }
  
  console.groupEnd();
};

const sanitizeJson = (text: string) => {
  if (!text) return "{}";
  let cleaned = text.replace(/```json/gi, "").replace(/```/g, "").trim();
  const firstBrace = cleaned.indexOf('{');
  const lastBrace = cleaned.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1) {
    cleaned = cleaned.substring(firstBrace, lastBrace + 1);
  }
  return cleaned;
};

const withRetry = async <T>(fn: () => Promise<T>, label: string, retries = 3): Promise<T> => {
  let lastError: any;
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (e: any) {
      lastError = e;
      const status = e?.status || (e?.message?.includes('500') ? 500 : (e?.message?.includes('429') ? 429 : 0));
      
      // 如果遇到 500 或 429，记录并重试
      if (status === 500 || status === 429 || e?.message?.toLowerCase().includes('limit')) {
        logApiError(e, `${label} (重试中 ${i + 1}/${retries})`);
        if (i < retries - 1) {
          const delay = 1500 * (i + 1); // 指数退避
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
      }
      throw e;
    }
  }
  throw lastError;
};

const extractMetrics = (res: any): TokenMetrics => {
  return {
    promptTokens: res.usageMetadata?.promptTokenCount || 0,
    candidateTokens: res.usageMetadata?.candidatesTokenCount || 0,
    totalTokens: res.usageMetadata?.totalTokenCount || 0
  };
};

// 加密接口参数
const encryptParams = () => {
  const timestamp = Math.floor(Date.now() / 1000)
  const sign = CryptoJS.SHA256(timestamp + 'a1b2c3d4e5f678909876543210fedcba').toString()
  return { sign, timestamp }
};

// Helper to call backend AI proxy
const callAiProxy = async (params: any) => {
  const getParams = { ...encryptParams(), ...params }
  const PROXY_URL = 'https://api.rapiddirect.com/api/69a79190d3eac';
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(getParams)
  });
  
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.message || 'AI Proxy Request Failed');
  }
  const { code, data, msg } = await response.json();
  if (code !== 1) throw new Error(msg || 'AI Proxy Request Failed');
  const text = data.candidates[0]?.content?.parts[0]?.text;
  return { text, ...data };
};

export const geminiService = {
  async extractProductName(idea: string, lang: Language): Promise<{ name: string, metrics: TokenMetrics }> {
    return withRetry(async () => {
      const response = await callAiProxy({
        model: "gemini-3-flash-preview",
        contents: `从这段创意描述中提炼一个最核心的产品/项目名称（不超过10个字）："${idea}"。仅返回名称本身。语言：${lang === 'zh' ? '中文' : 'English'}。`,
      });
      const m = extractMetrics(response);
      logTokenUsage("名称提炼", m);
      return { name: (response.text || "未命名项目").trim(), metrics: m };
    }, "名称提炼");
  },

  async generateFullPRD(idea: string, currentPrd: Record<string, any>, lang: Language): Promise<{ data: Record<string, any>, metrics: TokenMetrics }> {
    return withRetry(async () => {
      const response = await callAiProxy({
        model: "gemini-3-flash-preview",
        contents: `你是一位资深专业产品架构师。请根据用户的初步创意想法 "${idea}"，生成或完善一份结构化的 PRD。
        
        必须返回以下 16 个维度的 JSON 对象：
        1. project_name: 产品或项目名称
        2. core_description: 产品核心描述
        3. scenarios_pain_points: 使用场景与核心痛点
        4. user_persona: 目标用户画像
        5. appearance_materials: 外观偏好与材质质感
        6. feature_details: 核心功能细节 (数组格式)
        7. interaction_operation: 用户交互与操作
        8. key_components: 关键零部件定义 (数组格式)
        9. control_logic: 控制逻辑与软件
        10. performance_goals: 性能目标与效率指标
        11. cost_bom: 成本控制与BOM预算 (数组格式)
        12. mfg_quality: 制造品质与耐用性
        13. size_pkg: 尺寸限制与包装需求
        14. supply_chain: 供应链与材料约束
        15. compliance: 行业合规标准
        16. delivery_milestones: 交付时间节点

        当前上下文数据：${JSON.stringify(currentPrd)}。
        语言：${lang === 'zh' ? '中文' : 'English'}。
        输出格式：严格的 JSON。`,
        config: { responseMimeType: "application/json" }
      });
      const m = extractMetrics(response);
      logTokenUsage("架构师完善PRD", m);
      return { data: JSON.parse(sanitizeJson(response.text)), metrics: m };
    }, "PRD完善");
  },

  async generateQuestion(idea: string, history: { q: string, a: string }[], lang: Language, fieldName: string, isRefresh: boolean = false): Promise<{ data: any, metrics: TokenMetrics }> {
    const lastAnswer = history.length > 0 ? history[history.length - 1].a : null;
    return withRetry(async () => {
      const response = await callAiProxy({
        // Complex dialogue planning - use gemini-3-pro-preview
        model: "gemini-3-flash-preview", 
        contents: `你是一位工业设计与工程实现专家。
        你的任务是协助用户完善产品定义。
        维度： "${fieldName}"。
        核心创意： "${idea}"。
        ${lastAnswer ? `前序回答： "${lastAnswer}"。` : ""}

        沟通指令：语言极度精简，禁止发散，聚焦落地可行性，避免任何客套话。
        排版指令：使用 Markdown 格式（如加粗、列表）来提升关键信息的阅读性。
        ${isRefresh ? "特别要求：请提供 4 个比之前更具体、更具实战落地价值、更贴近真实生产制造的选项。避免虚无缥缈的概念，聚焦于具体的材质、结构、成本或功能实现方案。" : ""}
        输出格式要求：
        1. 提一个专业但通俗的简短问题。
        2. 提供 4 个具有工业实战价值的逻辑化选项。
        3. 选项中必须对关键词（如材质、结构、核心功能）使用 **加粗** 标记。
        4. 尽量少用专业术语，不用难懂词汇，普通人也能看懂。
        5. 选项文字严禁全部大写。
        6. 选项内容严禁包含任何数字编号、字母编号或前缀（如 1., 2., A., B., 0: 等）。

        语言： ${lang === 'zh' ? '中文' : 'English'}。
        期望JSON： { "question": "问题内容", "options": ["选项内容1", "选项内容2", "选项内容3", "选项内容4"] }。`,
        config: { responseMimeType: "application/json" }
      });
      const m = extractMetrics(response);
      logTokenUsage("专家引导提问", m);
      return { data: JSON.parse(sanitizeJson(response.text)), metrics: m };
    }, "引导提问");
  },

  // async generateImages(prdSummary: string, amount: number = 4, refinedPrompt?: string, contextImageB64?: string): Promise<{ urls: string[], metrics: TokenMetrics[] }> {
  //   // 使用默认的高质量工业设计占位图
  //   const defaultImages = [
  //     "https://picsum.photos/seed/industrial1/800/800",
  //     "https://picsum.photos/seed/industrial2/800/800",
  //     "https://picsum.photos/seed/industrial3/800/800",
  //     "https://picsum.photos/seed/industrial4/800/800",
  //   ];

  //   const selectedDefaults = defaultImages.slice(0, amount);

  //   const tasks = selectedDefaults.map(async (url, index) => {
  //     try {
  //       // 1. 获取图片数据 (Fetch image data)
  //       const imgRes = await fetch(url);
  //       const blob = await imgRes.blob();
        
  //       // 2. 创建 File 对象 (Create File object)
  //       const file = new File([blob], `render_${Date.now()}_${index}.jpg`, { type: 'image/jpeg' });
        
  //       // 3. 调用上传接口 (Call upload interface)
  //       const uploadRes = await dbService.uploadFile(file);
        
  //       if (uploadRes.code === 1 && uploadRes.data?.path) {
  //         return dbService.normalizeImageUrl(uploadRes.data.path);
  //       }
        
  //       console.warn("Upload failed, using original URL", uploadRes?.msg);
  //       return url;
  //     } catch (e) {
  //       console.error("Failed to process/upload default image", e);
  //       return url;
  //     }
  //   });

  //   const urls = await Promise.all(tasks);

  //   const metrics: TokenMetrics = { promptTokens: 0, candidateTokens: 0, totalTokens: 0 };
  //   logTokenUsage("视觉渲染", metrics, true);
    
  //   return { 
  //     urls, 
  //     metrics: [metrics] 
  //   };
  // },

  async generateImages(prdSummary: string, amount: number = 4, refinedPrompt?: string, oldContextImageUrl?: string): Promise<{ urls: string[], metrics: TokenMetrics[] }> {
    const oldBase64Img = oldContextImageUrl ? await imageUrlToBase64(oldContextImageUrl) : undefined;
    const coreConstraints = "photorealistic high-end industrial design, high quality, 4k. ABSOLUTELY NO TEXT, NO LABELS.";
    const whiteBackground = "pure white background, isolated, studio lighting.";
    const lifestyleBackground = "realistic lifestyle context, natural lighting.";
    
    const tasks = Array.from({ length: amount }).map(async (_, index) => {
      const isLifestyle = index >= 2;
      const scenePrompt = isLifestyle ? lifestyleBackground : whiteBackground;
      const basePrompt = refinedPrompt 
        ? `TASK: Modify the existing design based on this instruction: "${refinedPrompt}". 
           CORE PRODUCT: ${prdSummary.substring(0, 300)}. 
           STYLE: ${scenePrompt} ${coreConstraints}` 
        : `Product render: ${prdSummary.substring(0, 300)}. ${scenePrompt} ${coreConstraints}`;
      return withRetry(async () => {
        const parts: any[] = [{ text: basePrompt }];
        if (oldBase64Img?.includes(',')) {
          parts.unshift({ inlineData: { data: oldBase64Img.split(',')[1], mimeType: 'image/png' } });
        } else if (oldBase64Img) {
          parts.unshift({ inlineData: { data: oldBase64Img, mimeType: 'image/png' } });
        }
        
        const res = await callAiProxy({
          model: 'gemini-2.5-flash-image',
          contents: { parts },
          config: { imageConfig: { aspectRatio: "1:1" } }
        });

        const m = extractMetrics(res);
        const p = res.candidates?.[0]?.content?.parts.find((it: any) => it.inlineData);
        if (!p) throw new Error("No image data in response");

        const base64Data = p.inlineData.data;
        const mimeType = p.inlineData.mimeType || 'image/png';

        // Convert base64 to File
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const file = new File([blob], `render_${Date.now()}_${index}.png`, { type: mimeType });
        // Upload to server
        try {
          const uploadRes = await dbService.uploadFile(file);
          if (uploadRes.code === 1 && uploadRes.data?.path) {
            return { url: dbService.normalizeImageUrl(uploadRes.data.path), metrics: m };
          }
          // return { url: `data:${mimeType};base64,${base64Data}`, metrics: m };
          return { url: '' };
        } catch (uploadErr) {
          console.error("Image upload failed", uploadErr);
          return { url: '' };
        }
      }, `视觉渲染 #${index + 1}`);
    });
    const results = await Promise.all(tasks);
    try {
      if (results.length === 0) throw new Error("No images were generated");
      
      const urls = results.map(r => r!.url);
      const batchMetrics = results.map(r => r!.metrics);
      
      // 统一记录整组任务的总消耗，确保 lastCost 反映总额
      const totalBatchMetrics: TokenMetrics = {
        promptTokens: batchMetrics.reduce((sum, m) => sum + m.promptTokens, 0),
        candidateTokens: batchMetrics.reduce((sum, m) => sum + m.candidateTokens, 0),
        totalTokens: batchMetrics.reduce((sum, m) => sum + m.totalTokens, 0),
      };
      
      // 我们手动更新 sessionStats 以反映整组图片的成本
      // 这里的逻辑是：每张图 $0.02，总共 results.length * $0.02
      const batchCost = results.length * PRICING.IMAGE_PER_REQUEST;
      
      // 调用 logTokenUsage 来更新累计总额和控制台显示
      // 注意：这里我们传入一个特殊的 label，并确保 sessionStats.lastCost 是总额
      logTokenUsage(`视觉渲染(批量 x${results.length})`, totalBatchMetrics, false); // 先按文本记录以更新 tokens
      sessionStats.totalCost -= (totalBatchMetrics.promptTokens * PRICING.TEXT_INPUT + totalBatchMetrics.candidateTokens * PRICING.TEXT_OUTPUT); // 扣除刚才误加的文本费
      sessionStats.totalCost += batchCost; // 加上正确的图片费
      sessionStats.lastCost = batchCost; // 确保最后一次消耗是总额
      
      return { urls, metrics: batchMetrics };
    } catch (e) {
      console.error("Batch image generation failed", e);
      throw e;
    }
  },

  async analyzeDesignRequirement(idea: string, requirement: string, lang: Language, context?: { question: string, options: string[] }): Promise<{ text: string, metrics: TokenMetrics }> {
    return withRetry(async () => {
      const response = await callAiProxy({
        model: "gemini-3-flash-preview",
        contents: `你是一位工业设计与工程实现专家。
        用户提出了一个新的设计需求或疑问。
        初始创意： "${idea}"。
        ${context ? `当前引导问题： "${context.question}"。
        当前选项： ${context.options.join(', ')}。` : ""}
        用户输入： "${requirement}"。

        任务：根据初始创意、当前上下文和用户输入进行分析，给出简单易懂的解释与建议。
        排版指令：作为专业 UI 设计师，请使用 Markdown 格式（如使用序号、加粗、列表等）来显著提升内容的阅读性。确保结构清晰，重点突出，禁止使用表格。
        要求：
        1. 语言极度精简，禁止发散，禁止长篇大论。
        2. 如果用户提供了具体的指令（如：请使用xx材质、我想要使用红色等），请先以亲切的语气肯定用户的想法，然后帮助用户将这些需求转化为更专业、更具描述性的工业设计提示词（Prompt），并解释为什么这样优化更好。
        3. **特别要求**：如果用户提供了具体指令，请在回复中包含一个专门的标签 <optimized_prompt>这里是优化后的英文提示词</optimized_prompt>，并在回复的最后，以非常亲切的语气询问用户：“是否确认采用此方案并生成预览图？（此操作将消耗 4 积分）”。
        4. 如果用户是在针对当前的引导问题或选项提问（如：什么是选项2？为什么选这个？），请优先给出专业且易懂的解答。
        5. 如果用户提出的是新的设计需求，请给出 2 条可落地的专业建议，聚焦：造型、结构、材料、工艺、量产可行性。
        6. 若用户输入与工业设计无关，礼貌引导用户提出工业设计相关问题，不回答无关内容。
        7. 尽量少用专业术语，不用难懂词汇，普通人也能看懂。
        8. 在回复的句尾（如果没有触发第3条的询问），添加一句简短的话，引导用户可以继续提问或提供更多设计需求。
        9. 必须使用 Markdown 格式输出。

        语言： ${lang === 'zh' ? '中文' : 'English'}。`,
        
      });
      const m = extractMetrics(response);
      logTokenUsage("需求分析", m);
      return { text: response.text || "", metrics: m };
    }, "需求分析");
  },

  getTotalCost(): number {
    return sessionStats.totalCost;
  },

  getLastCost(): number {
    return sessionStats.lastCost;
  },

  setInitialCost(cost: number) {
    sessionStats.totalCost = cost;
  }
};
